
import { createContext, useContext, useState, useEffect, useRef } from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    token: localStorage.getItem('token'),
    isAuthenticated: !!localStorage.getItem('token'),
  });

  const inactivityTimeoutRef = useRef(null);

  // Inactivity timeout duration: 3 minutes (180,000 milliseconds)
  const INACTIVITY_TIMEOUT = 3 * 60 * 1000;

  const login = (token) => {
    localStorage.setItem('token', token);
    setAuthState({
      token,
      isAuthenticated: true,
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('bookmarkId');
    localStorage.removeItem('permissions');
    localStorage.removeItem('role');
    setAuthState({
      token: null,
      isAuthenticated: false,
    });

    // Clear the inactivity timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  };

  // Reset the inactivity timeout
  const resetInactivityTimeout = () => {
    // Clear existing timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Only set timeout if user is authenticated
    if (authState.isAuthenticated) {
      inactivityTimeoutRef.current = setTimeout(() => {
        console.log('User inactive for 3 minutes - logging out automatically');
        logout();
        // Redirect to login page
        window.location.href = '/';
      }, INACTIVITY_TIMEOUT);
    }
  };

  // Set up activity listeners
  useEffect(() => {
    if (!authState.isAuthenticated) {
      return;
    }

    // List of events to track user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Reset timeout on any activity
    const handleActivity = () => {
      resetInactivityTimeout();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize the timeout
    resetInactivityTimeout();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [authState.isAuthenticated]);

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
